import React from 'react'
import _ from 'lodash'
import { Card, Divider, IconButton, Stack, Typography } from '@mui/material'
import { RemoveCircle } from '@mui/icons-material'

import OptionsItemList from './OptionsItemList'
import QuantitySelect from '@/components/QuantitySelect'
import { CanteenCartItem } from '../types'
import { calculateCartItemTotal } from '../cartHelpers'
import useTenant from '@/hooks/useTenant'
import { numberFormat } from '@/helpers/numbers'
import Image from 'next/image'

type Props = {
  cartItem: CanteenCartItem
  onUpdate?: (cart: CanteenCartItem) => void
  onRemove?: (itemReference: string) => void
  showQuantity?: boolean
  ordered?: boolean
  hidePrices: boolean
  disableIncreaseQuantity?: boolean
}

const CartItem = ({
  cartItem,
  onUpdate,
  onRemove = () => {},
  showQuantity,
  ordered,
  disableIncreaseQuantity,
  hidePrices,
}: Props) => {
  const tenant = useTenant()
  const onCartItemUpdated = (qty: number) => {
    const updatedItem = _.cloneDeep(cartItem)
    updatedItem.qty = qty
    onUpdate?.(updatedItem)
  }

  const onCartItemRemoved = () => {
    onRemove(cartItem.reference)
  }

  const getItemName = (item: { name: string }, length: number) => {
    const name = item.name
    if (length && name && name.length > length) {
      return `${name.slice(0, length)}...`
    }
    return name
  }

  const { qty, options, item } = cartItem
  const hasOptions = options?.length > 0

  const descriptionLength = showQuantity ? 40 : 55
  const itemDescription = ordered ? cartItem.name : getItemName(item, descriptionLength)

  const totalPrice = calculateCartItemTotal(cartItem)

  const imageUrl = item?.image || tenant?.marketing.assets?.placeholder || ''

  return (
    <Card>
      <Stack padding={1}>
        <Stack spacing={1} alignItems="center" direction="row" paddingBottom={1}>
          <Image
            style={{ borderRadius: 6 }}
            width={55}
            height={55}
            alt={item?.name}
            src={imageUrl}
          />
          <Typography variant="body1">{itemDescription}</Typography>
        </Stack>
        {hasOptions ? (
          <>
            <Divider />
            <OptionsItemList optionsItemList={options} />
          </>
        ) : null}
        <Divider />
        <Stack
          spacing={1}
          alignItems="center"
          paddingTop={1}
          direction="row"
          justifyContent="space-between"
        >
          {!ordered ? (
            <Stack direction="row" spacing={1}>
              <QuantitySelect
                max={disableIncreaseQuantity ? qty : undefined}
                min={1}
                quantity={qty}
                onChange={onCartItemUpdated}
              />
              <IconButton size="medium" onClick={() => onCartItemRemoved()}>
                <RemoveCircle color="error" fontSize="medium" />
              </IconButton>
            </Stack>
          ) : (
            <Typography variant="body1"> </Typography>
          )}
          {!hidePrices ? (
            <Typography variant="body1">
              {qty} x {numberFormat(totalPrice / qty)}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  )
}

export default CartItem
